import ExcelJS from "exceljs";

export async function exportSalesPerSegmentXLSX({
  brandName = "Angels Pizza",
  storeName,
  startDate,
  endDate,
  categories,
  colors,
  counts, // array aligned with categories (loyalistCount, ...)
  fileName = "Sales_Per_Segment.xlsx",
}) {
  if (!categories?.length || !counts?.length) {
    console.error("exportSalesPerSegmentXLSX: missing categories/counts");
    return;
  }

  const total = counts.reduce((a, b) => a + Number(b || 0), 0) || 0;

  // percent numbers in 0..1 for Excel formatting
  const percents = counts.map((c) => (total ? Number(c || 0) / total : 0));

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Sales per Segment", {
    views: [{ showGridLines: true }],
  });

  // ===== helpers =====
  const toARGB = (hex) => {
    // "#rrggbb" -> "FFrrggbb"
    const h = (hex || "").replace("#", "").trim();
    if (h.length === 6) return `FF${h.toUpperCase()}`;
    return "FF000000";
  };

  const setBorder = (cell, thick = false) => {
    const style = thick ? "medium" : "thin";
    cell.border = {
      top: { style },
      left: { style },
      bottom: { style },
      right: { style },
    };
  };

  const dateRangeLabel =
    startDate && endDate ? `${startDate} to ${endDate}` : "Not set";

  // ===== layout columns =====
  // A: Category, B: Sales %
  sheet.getColumn(1).width = 32;
  sheet.getColumn(2).width = 16;

  // ===== Title =====
  sheet.mergeCells("A1:B1");
  const title = sheet.getCell("A1");
  title.value = "Sales Percentage per Customer Type";
  title.font = { bold: true, size: 18 };
  title.alignment = { vertical: "middle", horizontal: "left" };
  sheet.getRow(1).height = 28;

  // ===== Meta =====
  sheet.mergeCells("A2:B2");
  sheet.getCell("A2").value = `Store: ${storeName || "All Stores"}`;
  sheet.getCell("A2").font = { size: 12, color: { argb: "FF374151" } };
  sheet.getCell("A2").alignment = { vertical: "middle", horizontal: "left" };

  sheet.mergeCells("A3:B3");
  sheet.getCell("A3").value = `Date Range: ${dateRangeLabel}`;
  sheet.getCell("A3").font = { size: 12, color: { argb: "FF374151" } };
  sheet.getCell("A3").alignment = { vertical: "middle", horizontal: "left" };

  // Spacer row
  sheet.getRow(4).height = 10;

  // ===== Header Row =====
  const headerRowIdx = 5;
  sheet.getRow(headerRowIdx).height = 22;

  const hA = sheet.getCell(`A${headerRowIdx}`);
  const hB = sheet.getCell(`B${headerRowIdx}`);

  hA.value = "Category";
  hB.value = "Sales %";

  [hA, hB].forEach((c) => {
    c.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    c.alignment = { vertical: "middle", horizontal: "center" };
    c.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2F2F2F" },
    };
    setBorder(c, true);
  });

  // ===== Data Rows =====
  const startDataRow = headerRowIdx + 1;

  categories.forEach((cat, i) => {
    const rowIdx = startDataRow + i;

    sheet.getRow(rowIdx).height = 22;

    const cA = sheet.getCell(`A${rowIdx}`);
    const cB = sheet.getCell(`B${rowIdx}`);

    // Category cell with fill color
    cA.value = cat;
    cA.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    cA.alignment = { vertical: "middle", horizontal: "left" };
    cA.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: toARGB(colors?.[i] || "#111827") },
    };

    // Sales % cell
    cB.value = percents[i]; // 0..1
    cB.numFmt = "0.00%";
    cB.font = { bold: false, size: 12 };
    cB.alignment = { vertical: "middle", horizontal: "center" };

    // Borders (thin) for each cell
    setBorder(cA, false);
    setBorder(cB, false);
  });

  // ===== TOTAL Row =====
  const totalRowIdx = startDataRow + categories.length;
  sheet.getRow(totalRowIdx).height = 24;

  const tA = sheet.getCell(`A${totalRowIdx}`);
  const tB = sheet.getCell(`B${totalRowIdx}`);

  tA.value = "TOTAL";
  tB.value = percents.reduce((a, b) => a + b, 0); // should be 1.0
  tB.numFmt = "0.00%";

  // Green total style like your screenshot
  const totalFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6DA544" },
  };

  [tA, tB].forEach((c) => {
    c.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    c.alignment = { vertical: "middle", horizontal: "center" };
    c.fill = totalFill;
    setBorder(c, true);
  });

  // Optional: small note/footer (brand)
  const footerRowIdx = totalRowIdx + 2;
  sheet.mergeCells(`A${footerRowIdx}:B${footerRowIdx}`);
  const footer = sheet.getCell(`A${footerRowIdx}`);
  footer.value = `Brand: ${brandName}`;
  footer.font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
  footer.alignment = { vertical: "middle", horizontal: "left" };

  // ===== Download =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);

  // safe filename
  const safeStore = (storeName || "AllStores").replaceAll(/[^\w\-]/g, "_");
  const safeRange = dateRangeLabel.replaceAll(/[^\w\-]/g, "_");
  link.download = fileName || `SalesPerSegment_${safeStore}_${safeRange}.xlsx`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
